import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Users, Trophy, UserPlus, BarChart3 } from "lucide-react";
import Team from "@/pages/Team";
import TeamLeaderboard from "@/pages/TeamLeaderboard";
import TeamAnalytics from "@/pages/TeamAnalytics";
import { useAuth } from "@/context/AuthContext";

/**
 * Unified team hub. Query param ?tab=roster|leaderboard controls the
 * active tab; both /team and /leaderboard route here so bookmarks still work.
 */
export default function TeamHub() {
  const [params, setParams] = useSearchParams();
  const { can } = useAuth();
  const canManage = can ? can("team.manage") : false;
  const [inviteOpen, setInviteOpen] = useState(false);
  const tab = params.get("tab");
  const activeTab = ["leaderboard", "analytics"].includes(tab) ? tab : "roster";

  const setTab = (v) => {
    const next = new URLSearchParams(params);
    if (v === "roster") next.delete("tab");
    else next.set("tab", v);
    setParams(next, { replace: true });
  };

  return (
    <div data-testid="team-hub-page">
      <div className="px-6 md:px-10 pt-6 md:pt-10 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="app-eyebrow">/ Team</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3">Team hub</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
            Manage your teammates and see who&apos;s dialing, texting and closing the
            most this week — all in one place.
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => {
              // If on leaderboard tab, jump to roster first so the dialog can mount.
              if (activeTab !== "roster") setTab("roster");
              setTimeout(() => setInviteOpen(true), 50);
            }}
            className="app-btn app-btn-primary mt-2 shrink-0"
            data-testid="hub-invite-btn"
          >
            <UserPlus size={14} /> Invite Team Member
          </button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setTab} className="mt-4">
        <div className="px-6 md:px-10 border-b border-[var(--app-line)]">
          <TabsList className="bg-transparent gap-1 h-auto p-0">
            <TabsTrigger
              value="roster"
              data-testid="tab-roster"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[var(--app-black)] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2 text-sm"
            >
              <Users size={13} className="mr-1.5" /> Roster & roles
            </TabsTrigger>
            <TabsTrigger
              value="leaderboard"
              data-testid="tab-leaderboard"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[var(--app-black)] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2 text-sm"
            >
              <Trophy size={13} className="mr-1.5" /> Leaderboard
            </TabsTrigger>
            <TabsTrigger
              value="analytics"
              data-testid="tab-analytics"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[var(--app-black)] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2 text-sm"
            >
              <BarChart3 size={13} className="mr-1.5" /> Analytics
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="roster" className="mt-0">
          <Team hideHeader forceInviteOpen={inviteOpen} onInviteOpenChange={setInviteOpen} />
        </TabsContent>
        <TabsContent value="leaderboard" className="mt-0">
          <TeamLeaderboard hideHeader />
        </TabsContent>
        <TabsContent value="analytics" className="mt-0">
          <TeamAnalytics hideHeader />
        </TabsContent>
      </Tabs>
    </div>
  );
}
